// src/component/AdminGroupChats2026Stats.js
//
// 2026 グループチャット集計 管理画面（/admin/group-chats-2026-stats）
//
// 仕様：
//   - groupActivity2026Groups を全件取得し、各グループの
//     groupChats2026/{groupId}/aggregates/summary を読み込んで表示
//   - 「全グループ再集計」/ 行ごと「再集計」ボタンで
//     クライアント側でメッセージを走査して集計し、summary doc に書き込む
//   - 集計値: トータルメッセージ / 総いいね / 画像投稿数 / 最終アクティビティ
//   - 展開でユーザー別（メッセージ / 画像 / 受いいね / 送いいね）

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Collapse,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  LinearProgress,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import * as XLSX from "xlsx";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";

import { AppThemeContext } from "./AppShell";

const CONCURRENCY = 5;

const SORT_OPTIONS = [
  { key: "name", label: "グループ名" },
  { key: "totalMessages", label: "メッセージ数" },
  { key: "totalLikes", label: "いいね数" },
  { key: "totalImages", label: "画像数" },
  { key: "lastActivityAt", label: "最終アクティビティ" },
  { key: "updatedAt", label: "集計日" },
  { key: "memberCount", label: "メンバー数" },
];

function getSortValue(g, key) {
  const s = g.summary || {};
  switch (key) {
    case "name":
      return g.name || "";
    case "totalMessages":
      return s.totalMessages || 0;
    case "totalLikes":
      return s.totalLikes || 0;
    case "totalImages":
      return s.totalImages || 0;
    case "lastActivityAt":
      return s.lastActivityAt?.toMillis?.() || 0;
    case "updatedAt":
      return s.updatedAt?.toMillis?.() || 0;
    case "memberCount":
      return g.memberCount || 0;
    default:
      return 0;
  }
}

function compareGroups(a, b, key, dir) {
  const va = getSortValue(a, key);
  const vb = getSortValue(b, key);
  let cmp;
  if (typeof va === "string" || typeof vb === "string") {
    cmp = String(va).localeCompare(String(vb), "ja");
  } else {
    cmp = (va || 0) - (vb || 0);
  }
  return dir === "asc" ? cmp : -cmp;
}

function formatDateTime(ts) {
  const d = ts?.toDate?.();
  if (!d) return "—";
  return d.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// 1グループ分の集計をクライアント側で実行
async function aggregateOneGroupClient(groupId, callerUid) {
  const groupSnap = await getDoc(doc(db, "groupActivity2026Groups", groupId));
  const groupData = groupSnap.exists() ? groupSnap.data() : {};
  const groupName = groupData?.name || groupId;
  const members = Array.isArray(groupData?.members) ? groupData.members : [];
  const nameByUid = {};
  members.forEach((m) => {
    if (m?.uid) nameByUid[m.uid] = m.name || "";
  });

  let totalMessages = 0;
  let totalMessagesIncludingDeleted = 0;
  let lastActivityMs = 0;
  const byUser = {};
  const ensureUser = (uid) => {
    if (!byUser[uid]) {
      byUser[uid] = {
        name: nameByUid[uid] || "",
        messages: 0,
        images: 0,
        likesReceived: 0,
        likesGiven: 0,
      };
    }
    return byUser[uid];
  };

  const msgsSnap = await getDocs(
    collection(db, "groupChats2026", groupId, "messages")
  );

  msgsSnap.docs.forEach((d) => {
    const m = d.data();
    totalMessagesIncludingDeleted++;
    if (m?.deleted) return;
    totalMessages++;

    const uid = m?.uid;
    if (uid) {
      const u = ensureUser(uid);
      if (!u.name && m.name) u.name = m.name;
      u.messages++;
      const imageCount = Array.isArray(m.images)
        ? m.images.length
        : m.image
        ? 1
        : 0;
      u.images += imageCount;
      const likedBy = Array.isArray(m.likedBy) ? m.likedBy : [];
      u.likesReceived += likedBy.length;
      likedBy.forEach((likerUid) => {
        if (!likerUid) return;
        const l = ensureUser(likerUid);
        l.likesGiven++;
      });
    }
    const ts = m?.createdAt?.toMillis?.();
    if (ts && ts > lastActivityMs) lastActivityMs = ts;
  });

  const totalImages = Object.values(byUser).reduce(
    (s, u) => s + (u.images || 0),
    0
  );
  const totalLikes = Object.values(byUser).reduce(
    (s, u) => s + (u.likesReceived || 0),
    0
  );

  const summary = {
    groupId,
    groupName,
    memberCount: members.length,
    totalMessages,
    totalMessagesIncludingDeleted,
    totalImages,
    totalLikes,
    byUser,
    lastActivityAt: lastActivityMs ? Timestamp.fromMillis(lastActivityMs) : null,
    updatedAt: serverTimestamp(),
    updatedBy: callerUid || null,
  };

  await setDoc(
    doc(db, "groupChats2026", groupId, "aggregates", "summary"),
    summary
  );

  return {
    groupId,
    groupName,
    totalMessages,
    totalLikes,
    userCount: Object.keys(byUser).length,
  };
}

// 1グループ分の差分集計（前回の lastActivityAt 以降の新着メッセージだけ加算）
//   注意: 過去メッセージへのいいね追加 / 削除 はここでは反映されない
async function aggregateDifferentialOneGroupClient(groupId, callerUid) {
  const summaryRef = doc(
    db,
    "groupChats2026",
    groupId,
    "aggregates",
    "summary"
  );
  const summarySnap = await getDoc(summaryRef);

  // 初回（summary 未作成）は全件集計にフォールバック
  if (!summarySnap.exists()) {
    return aggregateOneGroupClient(groupId, callerUid);
  }

  const existing = summarySnap.data();
  const sinceMs = existing.lastActivityAt?.toMillis?.() || 0;

  // 仲間タブの最新メンバーリストでネームマップを更新
  const groupSnap = await getDoc(doc(db, "groupActivity2026Groups", groupId));
  const groupData = groupSnap.exists() ? groupSnap.data() : {};
  const groupName = groupData?.name || groupId;
  const members = Array.isArray(groupData?.members) ? groupData.members : [];
  const nameByUid = {};
  members.forEach((m) => {
    if (m?.uid) nameByUid[m.uid] = m.name || "";
  });

  // 新着分のクエリ（cursor は lastActivityAt）
  const msgsCol = collection(db, "groupChats2026", groupId, "messages");
  const newSnap = sinceMs
    ? await getDocs(
        query(
          msgsCol,
          where("createdAt", ">", Timestamp.fromMillis(sinceMs)),
          orderBy("createdAt", "asc")
        )
      )
    : await getDocs(query(msgsCol, orderBy("createdAt", "asc")));

  if (newSnap.empty) {
    // 新着 0 — タイムスタンプだけ更新
    await setDoc(
      summaryRef,
      {
        groupName,
        memberCount: members.length,
        updatedAt: serverTimestamp(),
        updatedBy: callerUid || null,
      },
      { merge: true }
    );
    return { groupId, groupName, newMessages: 0 };
  }

  // 既存値をベースに加算
  const byUser = JSON.parse(JSON.stringify(existing.byUser || {}));
  let totalMessages = existing.totalMessages || 0;
  let totalMessagesIncludingDeleted =
    existing.totalMessagesIncludingDeleted || 0;
  let lastActivityMs = sinceMs;

  const ensureUser = (uid) => {
    if (!byUser[uid]) {
      byUser[uid] = {
        name: nameByUid[uid] || "",
        messages: 0,
        images: 0,
        likesReceived: 0,
        likesGiven: 0,
      };
    }
    if (!byUser[uid].name && nameByUid[uid]) {
      byUser[uid].name = nameByUid[uid];
    }
    return byUser[uid];
  };

  let newMessagesCount = 0;
  newSnap.docs.forEach((d) => {
    const m = d.data();
    totalMessagesIncludingDeleted++;
    if (m?.deleted) return;
    totalMessages++;
    newMessagesCount++;

    const uid = m?.uid;
    if (uid) {
      const u = ensureUser(uid);
      if (!u.name && m.name) u.name = m.name;
      u.messages++;
      const imageCount = Array.isArray(m.images)
        ? m.images.length
        : m.image
        ? 1
        : 0;
      u.images += imageCount;
      const likedBy = Array.isArray(m.likedBy) ? m.likedBy : [];
      u.likesReceived += likedBy.length;
      likedBy.forEach((likerUid) => {
        if (!likerUid) return;
        const l = ensureUser(likerUid);
        l.likesGiven++;
      });
    }
    const ts = m?.createdAt?.toMillis?.();
    if (ts && ts > lastActivityMs) lastActivityMs = ts;
  });

  const totalImages = Object.values(byUser).reduce(
    (s, u) => s + (u.images || 0),
    0
  );
  const totalLikes = Object.values(byUser).reduce(
    (s, u) => s + (u.likesReceived || 0),
    0
  );

  await setDoc(summaryRef, {
    groupId,
    groupName,
    memberCount: members.length,
    totalMessages,
    totalMessagesIncludingDeleted,
    totalImages,
    totalLikes,
    byUser,
    lastActivityAt: lastActivityMs ? Timestamp.fromMillis(lastActivityMs) : null,
    updatedAt: serverTimestamp(),
    updatedBy: callerUid || null,
  });

  return {
    groupId,
    groupName,
    newMessages: newMessagesCount,
  };
}

// 簡易プール（最大並列数 N で関数を回す）
async function runWithConcurrency(items, n, fn, onProgress) {
  const queue = [...items];
  let done = 0;
  const workers = Array.from({ length: Math.min(n, queue.length) }, async () => {
    while (queue.length > 0) {
      const item = queue.shift();
      try {
        await fn(item);
      } catch (e) {
        console.error("aggregate error:", item, e);
      }
      done++;
      onProgress?.(done);
    }
  });
  await Promise.all(workers);
}

export default function AdminGroupChats2026Stats() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const appBg = theme.appBg || "#F5F5F9";
  const surface = theme.surfaceBg || "#FFFFFF";
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]); // [{ id, name, region, summary }]
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [running, setRunning] = useState(null); // null | "ALL" | groupId
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const groupsSnap = await getDocs(collection(db, "groupActivity2026Groups"));
      const items = await Promise.all(
        groupsSnap.docs.map(async (g) => {
          const data = g.data();
          let summary = null;
          try {
            const sumSnap = await getDoc(
              doc(db, "groupChats2026", g.id, "aggregates", "summary")
            );
            if (sumSnap.exists()) summary = sumSnap.data();
          } catch (e) {
            // 集計未作成は無視
          }
          return {
            id: g.id,
            name: data?.name || g.id,
            region: data?.region || "",
            memberCount: Array.isArray(data?.members) ? data.members.length : 0,
            summary,
          };
        })
      );
      items.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"));
      setGroups(items);
    } catch (e) {
      console.error("loadAll error:", e);
      setError("グループ一覧の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleRecomputeOne = async (groupId) => {
    setRunning(groupId);
    setError("");
    setInfo("");
    setProgress({ done: 0, total: 1 });
    try {
      await aggregateOneGroupClient(groupId, auth.currentUser?.uid);
      setInfo(
        `「${groups.find((g) => g.id === groupId)?.name || groupId}」を再集計しました`
      );
      await loadAll();
    } catch (e) {
      console.error("recompute one error:", e);
      setError(e?.message || "集計に失敗しました");
    } finally {
      setRunning(null);
      setProgress({ done: 0, total: 0 });
    }
  };

  const handleRecomputeAll = async () => {
    if (groups.length === 0) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `${groups.length} グループを再集計します。全件スキャンに時間と読み込みコストがかかります。よろしいですか？`
      )
    ) {
      return;
    }
    setRunning("ALL");
    setError("");
    setInfo("");
    setProgress({ done: 0, total: groups.length });
    const callerUid = auth.currentUser?.uid;
    try {
      await runWithConcurrency(
        groups.map((g) => g.id),
        CONCURRENCY,
        (gid) => aggregateOneGroupClient(gid, callerUid),
        (done) => setProgress({ done, total: groups.length })
      );
      setInfo(`全グループ再集計完了（${groups.length} 件）`);
      await loadAll();
    } catch (e) {
      console.error("recompute all error:", e);
      setError(e?.message || "集計に失敗しました");
    } finally {
      setRunning(null);
      setProgress({ done: 0, total: 0 });
    }
  };

  const handleDifferentialAll = async () => {
    if (groups.length === 0) return;
    setRunning("DIFF");
    setError("");
    setInfo("");
    setProgress({ done: 0, total: groups.length });
    const callerUid = auth.currentUser?.uid;
    let totalNew = 0;
    try {
      await runWithConcurrency(
        groups.map((g) => g.id),
        CONCURRENCY,
        async (gid) => {
          const r = await aggregateDifferentialOneGroupClient(gid, callerUid);
          totalNew += r?.newMessages || 0;
        },
        (done) => setProgress({ done, total: groups.length })
      );
      setInfo(
        `差分集計完了（${groups.length} グループ、新着 ${totalNew} 件）` +
          " ※ 過去メッセージへの新規いいね/削除は反映されません。"
      );
      await loadAll();
    } catch (e) {
      console.error("differential all error:", e);
      setError(e?.message || "差分集計に失敗しました");
    } finally {
      setRunning(null);
      setProgress({ done: 0, total: 0 });
    }
  };

  const filtered = groups
    .filter((g) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        (g.name || "").toLowerCase().includes(q) ||
        (g.region || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => compareGroups(a, b, sortKey, sortDir));

  const handleExportExcel = () => {
    if (filtered.length === 0) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1: グループ別サマリー
    const groupRows = filtered.map((g) => {
      const s = g.summary || {};
      return {
        "グループID": g.id,
        "グループ名": g.name || "",
        "地域": g.region || "",
        "メンバー数": g.memberCount || 0,
        "メッセージ数": s.totalMessages || 0,
        "削除込みメッセージ数": s.totalMessagesIncludingDeleted || 0,
        "総いいね数": s.totalLikes || 0,
        "画像投稿数": s.totalImages || 0,
        "最終アクティビティ": formatDateTime(s.lastActivityAt),
        "集計日時": formatDateTime(s.updatedAt),
      };
    });
    const groupSheet = XLSX.utils.json_to_sheet(groupRows);
    XLSX.utils.book_append_sheet(wb, groupSheet, "グループ別");

    // Sheet 2: ユーザー別
    const userRows = [];
    filtered.forEach((g) => {
      const s = g.summary;
      if (!s?.byUser) return;
      Object.entries(s.byUser).forEach(([uid, u]) => {
        userRows.push({
          "グループID": g.id,
          "グループ名": g.name || "",
          "UID": uid,
          "ユーザー名": u.name || "",
          "メッセージ数": u.messages || 0,
          "画像数": u.images || 0,
          "受いいね": u.likesReceived || 0,
          "送いいね": u.likesGiven || 0,
        });
      });
    });
    userRows.sort((a, b) => {
      const c = (a["グループ名"] || "").localeCompare(b["グループ名"] || "", "ja");
      if (c !== 0) return c;
      return (b["メッセージ数"] || 0) - (a["メッセージ数"] || 0);
    });
    const userSheet = XLSX.utils.json_to_sheet(userRows);
    XLSX.utils.book_append_sheet(wb, userSheet, "ユーザー別");

    const now = new Date();
    const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    const hm = `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
    XLSX.writeFile(wb, `groupChats2026_stats_${ymd}_${hm}.xlsx`);
  };

  const totalAcrossGroups = filtered.reduce(
    (acc, g) => {
      const s = g.summary || {};
      acc.messages += s.totalMessages || 0;
      acc.likes += s.totalLikes || 0;
      acc.images += s.totalImages || 0;
      return acc;
    },
    { messages: 0, likes: 0, images: 0 }
  );

  return (
    <Box sx={{ minHeight: "100%", background: appBg, pb: 6 }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: textBase, flex: 1 }}
          >
            2026 グループチャット 集計
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={handleExportExcel}
            disabled={loading || filtered.length === 0}
            sx={{
              borderColor: accent,
              color: accent,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { borderColor: accent, bgcolor: `${accent}10` },
            }}
          >
            Excel 出力
          </Button>
          <Tooltip title="前回集計時以降の新着メッセージだけ加算（軽い・速い／過去メッセージへの新規いいね・削除は反映されません）">
            <span>
              <Button
                variant="outlined"
                startIcon={
                  running === "DIFF" ? (
                    <CircularProgress size={16} sx={{ color: accent }} />
                  ) : (
                    <RefreshRoundedIcon />
                  )
                }
                onClick={handleDifferentialAll}
                disabled={running !== null || loading}
                sx={{
                  borderColor: accent,
                  color: accent,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: accent, bgcolor: `${accent}10` },
                }}
              >
                差分集計
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="全件スキャンしてゼロから集計（重い／いいね・削除も最新化）">
            <span>
              <Button
                variant="contained"
                startIcon={
                  running === "ALL" ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <RefreshRoundedIcon />
                  )
                }
                onClick={handleRecomputeAll}
                disabled={running !== null || loading}
                sx={{
                  bgcolor: accent,
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: accent, opacity: 0.92 },
                }}
              >
                全件再集計
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {(running === "ALL" || running === "DIFF") && progress.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: textBase }}>
                {running === "DIFF" ? "差分集計中" : "全件集計中"}… {progress.done} / {progress.total}
              </Typography>
              <Typography variant="caption" sx={{ color: textBase, opacity: 0.7 }}>
                {Math.round((progress.done / progress.total) * 100)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(progress.done / progress.total) * 100}
              sx={{ height: 6, borderRadius: 999 }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo("")}>
            {info}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="グループ名・地域で絞り込み"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ flex: 1, maxWidth: 320 }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>ソート</InputLabel>
              <Select
                label="ソート"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={sortDir === "asc" ? "昇順" : "降順"}>
              <IconButton
                size="small"
                onClick={() =>
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                }
                sx={{ border: `1px solid ${border}` }}
              >
                {sortDir === "asc" ? (
                  <ArrowUpwardRoundedIcon fontSize="small" />
                ) : (
                  <ArrowDownwardRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14 }} />}
              label={`総 ${totalAcrossGroups.messages.toLocaleString()} 件`}
              size="small"
              sx={{ bgcolor: `${accent}10`, color: accent, fontWeight: 600 }}
            />
            <Chip
              icon={<FavoriteRoundedIcon sx={{ fontSize: 14 }} />}
              label={`いいね ${totalAcrossGroups.likes.toLocaleString()}`}
              size="small"
            />
            <Chip
              icon={<ImageRoundedIcon sx={{ fontSize: 14 }} />}
              label={`画像 ${totalAcrossGroups.images.toLocaleString()}`}
              size="small"
            />
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={32} sx={{ color: accent }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="body2" sx={{ color: textBase, opacity: 0.6 }}>
              該当グループがありません
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {filtered.map((g) => {
              const s = g.summary;
              const isExpanded = expanded === g.id;
              const isRunning = running === g.id;
              return (
                <Paper
                  key={g.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setExpanded(isExpanded ? null : g.id)}
                      >
                        <ExpandMoreRoundedIcon
                          sx={{
                            transform: isExpanded ? "rotate(180deg)" : "none",
                            transition: "0.2s",
                          }}
                        />
                      </IconButton>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: textBase,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: textBase, opacity: 0.55 }}
                        >
                          {g.region || ""}
                          {g.region && " · "}
                          メンバー {g.memberCount}
                          {s?.updatedAt && (
                            <>　集計: {formatDateTime(s.updatedAt)}</>
                          )}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {s ? (
                        <>
                          <Chip
                            size="small"
                            icon={
                              <ChatBubbleOutlineRoundedIcon
                                sx={{ fontSize: 14 }}
                              />
                            }
                            label={(s.totalMessages || 0).toLocaleString()}
                            sx={{
                              bgcolor: `${accent}10`,
                              color: accent,
                              fontWeight: 600,
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<FavoriteRoundedIcon sx={{ fontSize: 14 }} />}
                            label={(s.totalLikes || 0).toLocaleString()}
                          />
                          <Chip
                            size="small"
                            icon={<ImageRoundedIcon sx={{ fontSize: 14 }} />}
                            label={(s.totalImages || 0).toLocaleString()}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: textBase, opacity: 0.55, ml: 0.5 }}
                          >
                            最終: {formatDateTime(s.lastActivityAt)}
                          </Typography>
                        </>
                      ) : (
                        <Chip size="small" label="未集計" variant="outlined" />
                      )}
                      <Tooltip title="このグループだけ再集計">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRecomputeOne(g.id)}
                            disabled={running !== null}
                          >
                            {isRunning ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RefreshRoundedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ mt: 1.5 }}>
                      {s?.byUser && Object.keys(s.byUser).length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>
                                ユーザー
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                メッセージ
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                画像
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                受いいね
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                送いいね
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(s.byUser)
                              .sort(
                                (a, b) =>
                                  (b[1].messages || 0) - (a[1].messages || 0)
                              )
                              .map(([uid, u]) => (
                                <TableRow key={uid}>
                                  <TableCell
                                    sx={{
                                      maxWidth: 240,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {u.name || `(${uid.slice(0, 8)})`}
                                  </TableCell>
                                  <TableCell align="right">
                                    {u.messages || 0}
                                  </TableCell>
                                  <TableCell align="right">
                                    {u.images || 0}
                                  </TableCell>
                                  <TableCell align="right">
                                    {u.likesReceived || 0}
                                  </TableCell>
                                  <TableCell align="right">
                                    {u.likesGiven || 0}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: textBase, opacity: 0.55 }}
                        >
                          {s
                            ? "ユーザー別のデータがありません"
                            : "まだ集計されていません。右の「再集計」を押してください。"}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
